import type { GetMyJoinRequestsResponse, GetTripJoinRequestsResponse } from '@orbit/api-types'
import { getDeviceContext } from '@lib/analytics'
import api from '@lib/api'
import { membershipFailure } from '@lib/membership'
import { queryKeys } from '@lib/queryKeys'
import { QueryClient, UseMutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'

/**
 * Join requests and member removal. Every action posts this browser's device
 * context so the *server* writes its analytics event (ADR 0013). Built as option
 * factories so they can be unit-tested through a bare `MutationObserver`.
 */

/** A trip's pending Join requests. Owner only, so callers gate it with `enabled`. */
export const useTripJoinRequests = (tripId: string | undefined, enabled = true) =>
	useQuery<GetTripJoinRequestsResponse>({
		queryKey: queryKeys.joinRequests(tripId),
		queryFn: async () => {
			if (!tripId) throw new Error('Trip ID is required')
			const response = await api.get<GetTripJoinRequestsResponse>(`/trips/${tripId}/join-requests`)
			return response.data
		},
		enabled: enabled && !!tripId,
	})

/** My own Join requests, pending and denied. */
export const useMyJoinRequests = (enabled = true) =>
	useQuery<GetMyJoinRequestsResponse>({
		queryKey: queryKeys.myJoinRequests(),
		queryFn: async () => (await api.get<GetMyJoinRequestsResponse>('/trips/join-requests')).data,
		enabled,
	})

type MemberAction = UseMutationOptions<void, Error, { userId: number }>

const postAction = async (path: string): Promise<void> => {
	await api.post(path, { analytics: getDeviceContext() })
}

/**
 * The trip id comes off the route, so it can be missing for a beat; an action
 * fired then fails rather than posting to `/trips//…`.
 */
const tripPath = (tripId: string | undefined): string => {
	if (!tripId) throw new Error('Trip ID is required')
	return `/trips/${tripId}`
}

/** Refreshes everything a change of who is in the trip shows up in (`queryKeys.membershipKeys`). */
export const invalidateMembership = (queryClient: QueryClient, tripId: string | undefined) => {
	for (const queryKey of queryKeys.membershipKeys(tripId)) queryClient.invalidateQueries({ queryKey })
}

export const buildApproveJoinRequestOptions = (queryClient: QueryClient, tripId: string | undefined): MemberAction => ({
	mutationFn: ({ userId }) => postAction(`${tripPath(tripId)}/join-requests/${userId}/approve`),
	onSuccess: () => {
		queryClient.invalidateQueries({ queryKey: queryKeys.joinRequests(tripId) })
		invalidateMembership(queryClient, tripId)
	},
})

export const buildDenyJoinRequestOptions = (queryClient: QueryClient, tripId: string | undefined): MemberAction => ({
	mutationFn: ({ userId }) => postAction(`${tripPath(tripId)}/join-requests/${userId}/deny`),
	onSuccess: () => {
		queryClient.invalidateQueries({ queryKey: queryKeys.joinRequests(tripId) })
	},
})

/** Cancels my pending request or dismisses a denied one. By code: I'm not a member. */
export const buildCancelJoinRequestOptions = (
	queryClient: QueryClient,
): UseMutationOptions<void, Error, { code: string }> => ({
	mutationFn: ({ code }) => postAction(`/trips/${encodeURIComponent(code)}/join-request/cancel`),
	// Refresh either way: a failure may mean the request was already gone (404).
	onSettled: () => {
		queryClient.invalidateQueries({ queryKey: queryKeys.myJoinRequests() })
	},
})

export const buildRemoveMemberOptions = (queryClient: QueryClient, tripId: string | undefined): MemberAction => ({
	mutationFn: ({ userId }) => postAction(`${tripPath(tripId)}/members/${userId}/remove`),
	onSuccess: () => invalidateMembership(queryClient, tripId),
})

export const useApproveJoinRequest = (tripId: string | undefined) =>
	useMutation(buildApproveJoinRequestOptions(useQueryClient(), tripId))

export const useDenyJoinRequest = (tripId: string | undefined) =>
	useMutation(buildDenyJoinRequestOptions(useQueryClient(), tripId))

export const useCancelJoinRequest = () => useMutation(buildCancelJoinRequestOptions(useQueryClient()))

/**
 * Withdraws one of my requests — cancels a pending one or dismisses a denied one —
 * and says why when it doesn't take. A request that was already gone counts as
 * withdrawn, since that is what was asked for. `withdrawingCode` is the trip code
 * in flight, if any.
 */
export const useWithdrawJoinRequest = () => {
	const mutation = useCancelJoinRequest()

	const withdraw = (code: string, onWithdrawn?: () => void) =>
		mutation.mutate(
			{ code },
			{
				onSuccess: () => onWithdrawn?.(),
				onError: error => {
					const message = membershipFailure({ kind: 'cancelRequest' }, error)
					if (message) toast.error(message)
					else onWithdrawn?.()
				},
			},
		)

	return { withdraw, withdrawingCode: mutation.isPending ? (mutation.variables?.code ?? null) : null }
}

export const useRemoveMember = (tripId: string | undefined) =>
	useMutation(buildRemoveMemberOptions(useQueryClient(), tripId))
